import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekday from 'dayjs/plugin/weekday';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { DatePickerBase } from '@baseComponents/DateTimePicker';
import { SelectBase } from '@baseComponents/Select';
import { ContentCircularLoader, ContentContainer } from '@libComponents';
import fetchRequest from '@libs/fetchRequest';
import { theme as appTheme } from 'app/Theme';
import confetti from 'canvas-confetti';

// Setup dayjs plugins required by react-big-calendar
dayjs.extend(weekOfYear);
dayjs.extend(weekday);
dayjs.extend(localeData);
const localizer = dayjsLocalizer(dayjs);

// ---------- Constants ---------------------------------------------------------

const STATUS_COLORS = {
    approved: 'success',
    pending: 'warning',
    rejected: 'error',
    cancelled: 'default',
    canceled: 'default',
    open: 'warning'
};
const DEFAULT_CALENDAR_NAME = 'Bangladesh';
const DEFAULT_STATUS = 'Approved';
const HOLIDAY_FILTER_VALUE = '__holidays__';
const BIRTHDAY_FILTER_VALUE = '__birthdays__';
const ANNIVERSARY_FILTER_VALUE = '__anniversaries__';
const MONTH_FETCH_DEBOUNCE_MS = 1200;

// Curated, accessible color palette: light background + darker border/text.
const USER_COLOR_PALETTE = [
    { bg: '#E3F2FD', border: '#1565C0', text: '#0D47A1' },
    { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20' },
    { bg: '#FFF3E0', border: '#EF6C00', text: '#E65100' },
    { bg: '#F3E5F5', border: '#7B1FA2', text: '#4A148C' },
    { bg: '#FFEBEE', border: '#C62828', text: '#B71C1C' },
    { bg: '#E0F7FA', border: '#00838F', text: '#006064' },
    { bg: '#FFF8E1', border: '#F9A825', text: '#F57F17' },
    { bg: '#F1F8E9', border: '#558B2F', text: '#33691E' },
    { bg: '#EDE7F6', border: '#512DA8', text: '#311B92' },
    { bg: '#FCE4EC', border: '#AD1457', text: '#880E4F' },
    { bg: '#E8EAF6', border: '#283593', text: '#1A237E' },
    { bg: '#E0F2F1', border: '#00695C', text: '#004D40' }
];

// Dark-mode variant of the user colour palette (rich, low-saturation backgrounds,
// lighter borders & text so they read clearly on a dark surface).
const DARK_USER_COLOR_PALETTE = [
    { bg: '#1A2333', border: '#64B5F6', text: '#90CAF9' },
    { bg: '#1A2E1A', border: '#81C784', text: '#A5D6A7' },
    { bg: '#2E241A', border: '#FFB74D', text: '#FFCC80' },
    { bg: '#2A1B3E', border: '#CE93D8', text: '#CE93D8' },
    { bg: '#2E1A1A', border: '#EF9A9A', text: '#EF9A9A' },
    { bg: '#1A2E33', border: '#4DD0E1', text: '#80DEEA' },
    { bg: '#2E2A1A', border: '#FFD54F', text: '#FFE082' },
    { bg: '#1E2E1A', border: '#81C784', text: '#A5D6A7' },
    { bg: '#231A3E', border: '#B39DDB', text: '#B39DDB' },
    { bg: '#2E1A2A', border: '#F48FB1', text: '#F48FB1' },
    { bg: '#1A1E3E', border: '#7986CB', text: '#9FA8DA' },
    { bg: '#1A2E2E', border: '#4DB6AC', text: '#80CBC4' }
];

// Deterministic hash so the same user always maps to the same color.
const hashString = (str) => {
    let hash = 0;
    const s = String(str || '');
    for (let i = 0; i < s.length; i++) {
        hash = (hash << 5) - hash + s.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

const getUserColor = (key, isDark) => {
    const palette = isDark ? DARK_USER_COLOR_PALETTE : USER_COLOR_PALETTE;
    const idx = hashString((key || '').toLowerCase()) % palette.length;
    return palette[idx];
};

const normalizeLabel = (value) => String(value || '').trim().toLowerCase();

const LEAVE_TYPE_COLOR_MAP = {
    // ── Leave types: warm/earthy tones ──────────────────────────────────────
    // Casual — orange: light, everyday
    Casual:              { bg: '#FFF3E0', border: '#EF6C00', text: '#BF360C', dot: '#FF9800' },
    // Compensatory — purple: formal, earned-back
    'Compensatory Leave':{ bg: '#F3E5F5', border: '#7B1FA2', text: '#4A148C', dot: '#AB47BC' },
    // Earned — yellow/amber: valued, accumulated
    Earned:              { bg: '#FFFDE7', border: '#F9A825', text: '#F57F17', dot: '#FDD835' },
    // Maternity & Paternity — same pink: parental leave
    Maternity:           { bg: '#FCE4EC', border: '#C2185B', text: '#880E4F', dot: '#F06292' },
    Paternity:           { bg: '#FCE4EC', border: '#C2185B', text: '#880E4F', dot: '#F06292' },
    // Sick — red: medical/urgent
    Sick:                { bg: '#FFEBEE', border: '#D32F2F', text: '#B71C1C', dot: '#EF5350' },
    // ── Events/celebrations: cool tones ─────────────────────────────────────
    // Holiday — green: public celebration
    Holiday:             { bg: '#E8F5E9', border: '#2E7D32', text: '#1B5E20', dot: '#43A047' },
    // Birthday — cyan/teal: joyful personal milestone
    Birthday:            { bg: '#E0F7FA', border: '#00838F', text: '#006064', dot: '#26C6DA' },
    // Work Anniversary — blue: loyalty, achievement
    'Work Anniversary':  { bg: '#E3F2FD', border: '#1565C0', text: '#0D47A1', dot: '#42A5F5' },
};

// Dark-mode variant — deep, rich backgrounds with lighter text so the cards
// remain legible on the dark dialog/calendar surface.
const DARK_LEAVE_TYPE_COLOR_MAP = {
    Casual:              { bg: '#3E2723', border: '#FF9800', text: '#FFCC80', dot: '#FF9800' },
    'Compensatory Leave':{ bg: '#2A1B3E', border: '#CE93D8', text: '#CE93D8', dot: '#CE93D8' },
    Earned:              { bg: '#3E3520', border: '#FDD835', text: '#FFF59D', dot: '#FDD835' },
    Maternity:           { bg: '#3E1B2A', border: '#F06292', text: '#F48FB1', dot: '#F06292' },
    Paternity:           { bg: '#3E1B2A', border: '#F06292', text: '#F48FB1', dot: '#F06292' },
    Sick:                { bg: '#3E1A1A', border: '#EF5350', text: '#EF9A9A', dot: '#EF5350' },
    Holiday:             { bg: '#1B2E1B', border: '#66BB6A', text: '#A5D6A7', dot: '#66BB6A' },
    Birthday:            { bg: '#1A2E33', border: '#26C6DA', text: '#80DEEA', dot: '#26C6DA' },
    'Work Anniversary':  { bg: '#1A2333', border: '#42A5F5', text: '#90CAF9', dot: '#42A5F5' },
};

const LEAVE_TYPE_ALIASES = {
    casual: 'Casual',
    'casual leave': 'Casual',
    compensatory: 'Compensatory Leave',
    'compensatory leave': 'Compensatory Leave',
    earned: 'Earned',
    'earned leave': 'Earned',
    maternity: 'Maternity',
    'maternity leave': 'Maternity',
    paternity: 'Paternity',
    'paternity leave': 'Paternity',
    sick: 'Sick',
    'sick leave': 'Sick',
    holiday: 'Holiday',
    holidays: 'Holiday',
    'public holiday': 'Holiday',
    birthday: 'Birthday',
    birthdays: 'Birthday',
    anniversary: 'Work Anniversary',
    anniversaries: 'Work Anniversary',
    'work anniversary': 'Work Anniversary'
};

const LEAVE_TYPE_ORDER = [
    'Casual',
    'Compensatory Leave',
    'Earned',
    'Maternity',
    'Paternity',
    'Sick',
    'Holiday',
    'Birthday',
    'Work Anniversary'
];

const UNIFORM_AVATAR_STYLE = {
    bg: '#1565C0',
    text: '#FFFFFF',
    shadow: '#1565C055'
};

const normalizeLeaveTypeName = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return 'Leave';
    return LEAVE_TYPE_ALIASES[normalizeLabel(raw)] || raw;
};

const getLeaveTypeKey = (item) => {
    const t = item?.resource?.type || item?.type;
    if (t === 'holiday') return 'Holiday';
    if (t === 'birthday') return 'Birthday';
    if (t === 'anniversary') return 'Work Anniversary';
    return normalizeLeaveTypeName(
        item?.categoryName ||
            item?.leaveType ||
            item?.resource?.categoryName ||
            item?.resource?.leaveType ||
            'Leave'
    );
};

const getLeaveTypeColor = (item, isDark) => {
    const leaveType = getLeaveTypeKey(item);
    const map = isDark ? DARK_LEAVE_TYPE_COLOR_MAP : LEAVE_TYPE_COLOR_MAP;
    return map[leaveType] || getUserColor(leaveType, isDark);
};

const toCalendarStartDate = (value) => dayjs(value).startOf('day').toDate();

// react-big-calendar treats all-day event end values as exclusive.
const toCalendarEndDate = (value) =>
    dayjs(value).startOf('day').add(1, 'day').toDate();

const getLeaveSliceFraction = (leave) => {
    const totalNumberOfDays = Number(leave?.totalNumberOfDays);
    if (!Number.isFinite(totalNumberOfDays)) return 1;
    if (Number.isInteger(totalNumberOfDays)) return 1;

    const startDate = dayjs(leave?.startDate).startOf('day');
    const endDate = dayjs(leave?.endDate).startOf('day');
    const daySpan = Math.max(endDate.diff(startDate, 'day') + 1, 1);
    const fractionalPart = Number((totalNumberOfDays % 1).toFixed(2));

    if (daySpan === 1) {
        return Math.max(Math.min(totalNumberOfDays, 1), 0.25);
    }

    if (leave?.isLastDay && fractionalPart > 0) {
        return Math.max(Math.min(fractionalPart, 1), 0.25);
    }

    return 1;
};

// ---------- Helpers -----------------------------------------------------------

const getInitials = (nameOrEmail) => {
    const raw = (nameOrEmail || '').trim();
    if (!raw) return '?';
    const parts = raw
        .replace(/\s+/g, ' ')
        .split(' ')
        .filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }
    if (raw.includes('@')) {
        return raw.slice(0, 2).toUpperCase();
    }
    return raw.slice(0, 2).toUpperCase();
};

// ---------- Filters component (rendered inside ContentContainer header) ------

const EMPTY_META = { form: 'leave-calendar', touched: false };

const Filters = ({
    monthDate,
    onMonthDateChange,
    onPrevMonth,
    onNextMonth,
    calendars,
    isCalendarsLoading,
    onCalendarChange,
    calendarId,
    leaveTypes,
    selectedLeaveType,
    onLeaveTypeChange
}) => {
    const { spacing } = useTheme();

    const calendarData = useMemo(
        () => calendars.map((c) => ({ value: c.id, label: c.name || '' })),
        [calendars]
    );

    const defaultCalendarId = useMemo(() => {
        const bangladesh = calendarData.find(
            (option) => normalizeLabel(option.label) === normalizeLabel(DEFAULT_CALENDAR_NAME)
        );
        return bangladesh?.value || calendarData[0]?.value || '';
    }, [calendarData]);

    const leaveTypeData = useMemo(
        () => [
            { value: '', label: 'All types' },
            { value: HOLIDAY_FILTER_VALUE, label: 'Holidays' },
            { value: BIRTHDAY_FILTER_VALUE, label: 'Birthdays' },
            { value: ANNIVERSARY_FILTER_VALUE, label: 'Work Anniversaries' },
            ...leaveTypes.map((t) => ({ value: t, label: t }))
        ],
        [leaveTypes]
    );

    // Sync parent state when data loads and nothing is selected yet
    useEffect(() => {
        if (!defaultCalendarId) return;            // data not loaded yet
        if (calendarId) return;                    // parent already has a selection
        onCalendarChange(defaultCalendarId);
    }, [defaultCalendarId, calendarId, onCalendarChange]);

    const handleCalendarChange = useCallback(
        (value) => onCalendarChange(value || null),
        [onCalendarChange]
    );

    const handleLeaveTypeChange = useCallback(
        (value) => onLeaveTypeChange(value || null),
        [onLeaveTypeChange]
    );

    return (
        <Stack
            direction='row'
            spacing={0}
            alignItems='center'
            flexWrap='wrap'
            useFlexGap
            sx={{
                py: 0.5,
                gap: 1,
                '@media screen and (max-width: 600px)': { width: '100%' }
            }}
        >
            {/* Month picker with prev/next arrows */}
            <Stack
                direction='row'
                spacing={0.5}
                alignItems='center'
                justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
                sx={{ width: { xs: '100%', sm: 'auto' }, pb: 1.25 }}
            >
                <IconButton
                    size='small'
                    onClick={onPrevMonth}
                    aria-label='Previous month'
                >
                    <KeyboardArrowLeftIcon fontSize='small' />
                </IconButton>
                <Box sx={{ flex: { xs: 1, sm: 'unset' }, width: { xs: '100%', sm: 140 } }}>
                    <DatePickerBase
                        name='leave-calendar-month'
                        fieldName='leave-calendar-month'
                        views={['year', 'month']}
                        openTo='month'
                        inputFormat='MMM YYYY'
                        input={{
                            value: monthDate,
                            onChange: onMonthDateChange
                        }}
                        maxDate={dayjs().add(1, 'year').endOf('year')}
                        minDate={dayjs('2022-01-01')}
                        meta={EMPTY_META}
                        styles={{ input: { height: spacing(4.5) } }}
                    />
                </Box>
                <IconButton
                    size='small'
                    onClick={onNextMonth}
                    aria-label='Next month'
                >
                    <KeyboardArrowRightIcon fontSize='small' />
                </IconButton>
            </Stack>

            {/* Calendar selector */}
            <Box sx={{ width: { xs: '100%', sm: 200 } }}>
                <SelectBase
                    key={defaultCalendarId || 'loading'}
                    fieldName='calendar'
                    meta={EMPTY_META}
                    data={calendarData}
                    loading={isCalendarsLoading}
                    placeholder='Select calendar'
                    input={{
                        value: calendarId ?? defaultCalendarId,
                        onChange: handleCalendarChange
                    }}
                />
            </Box>

            {/* Leave type filter — always shown; fixed options include Holidays, Birthdays, Work Anniversaries */}
            <Box sx={{ width: { xs: '100%', sm: 185 } }}>
                <SelectBase
                    fieldName='leave-type'
                    meta={EMPTY_META}
                    data={leaveTypeData}
                    placeholder='All types'
                    input={{
                        value: selectedLeaveType ?? '',
                        onChange: handleLeaveTypeChange
                    }}
                />
            </Box>
        </Stack>
    );
};

// ---------- Custom event component for react-big-calendar --------------------

const CalEventContent = ({ event }) => {
    const kind = event.resource?.type;
    const name = event.title || '';
    if (kind === 'holiday') {
        return (
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                🎉 {name}
            </span>
        );
    }
    if (kind === 'birthday') {
        return (
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                🎂 {name}
            </span>
        );
    }
    if (kind === 'anniversary') {
        return (
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                🎊 {name}
            </span>
        );
    }
    return (
        <span style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <span
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                    width: '100%'
                }}
            >
                {name}
            </span>
        </span>
    );
};

// ---------- Day details dialog body ------------------------------------------

const DayDetails = ({ day, onCelebrate }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const holidays = day.holidays ?? [];
    const leaves = day.leaves ?? [];
    const birthdays = day.birthdays ?? [];
    const anniversaries = day.anniversaries ?? [];
    if (
        holidays.length === 0 &&
        leaves.length === 0 &&
        birthdays.length === 0 &&
        anniversaries.length === 0
    ) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                    No events recorded for this day.
                </Typography>
            </Box>
        );
    }
    const colorMap = isDark ? DARK_LEAVE_TYPE_COLOR_MAP : LEAVE_TYPE_COLOR_MAP;
    const birthdayColor = colorMap.Birthday;
    const anniversaryColor = colorMap['Work Anniversary'];
    const hasAnyAbove = holidays.length > 0;
    const hasBirthdays = birthdays.length > 0;
    const hasAnniversaries = anniversaries.length > 0;
    return (
        <Box>
            {/* Holidays always first */}
            {holidays.length > 0 && (
                <Box sx={{ mb: leaves.length > 0 || hasBirthdays || hasAnniversaries ? 2.5 : 0 }}>
                    <Typography
                        variant='overline'
                        sx={{ fontWeight: 700, letterSpacing: 1.2, color: isDark ? '#FFB74D' : '#E65100', display: 'block', mb: 1 }}
                    >
                        🎉 Public Holidays
                    </Typography>
                    <Stack spacing={1}>
                        {holidays.map((h) => {
                            const holidayColors = isDark
                                ? { bg: '#2E241A', border: '#FFB74D', text: '#FFCC80' }
                                : { bg: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)', border: '#FFCC80', text: '#BF360C' };
                            return (
                                <Box
                                    key={h.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        background: holidayColors.bg,
                                        border: `1px solid ${holidayColors.border}`
                                    }}
                                >
                                    <Box sx={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>🎉</Box>
                                    <Box>
                                        <Typography variant='body2' sx={{ fontWeight: 700, color: holidayColors.text }}>
                                            {h.name || h.title}
                                        </Typography>
                                        {h.description && (
                                            <Typography variant='caption' color={isDark ? '#FFCC80' : 'warning.dark'}>
                                                {h.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}

            {/* Birthdays */}
            {hasBirthdays && (
                <Box sx={{ mb: hasAnniversaries || leaves.length > 0 ? 2.5 : 0 }}>
                    {hasAnyAbove && <Divider sx={{ mb: 2 }} />}
                    <Typography
                        variant='overline'
                        sx={{ fontWeight: 700, letterSpacing: 1.2, color: birthdayColor.text, display: 'block', mb: 1 }}
                    >
                        🎂 Birthdays
                    </Typography>
                    <Stack spacing={1}>
                        {birthdays.map((b) => (
                            <Box
                                key={`bd-${b.employeeEmail}`}
                                onClick={() => onCelebrate?.({ type: 'birthday', name: b.employeeName || b.employeeEmail })}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    background: birthdayColor.bg,
                                    border: `1px solid ${birthdayColor.border}`,
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.15s, transform 0.15s',
                                    '&:hover': {
                                        boxShadow: `0 4px 16px ${birthdayColor.border}55`,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                <Box sx={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>🎂</Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant='body2' sx={{ fontWeight: 700, color: birthdayColor.text }}>
                                        {b.employeeName || b.employeeEmail}
                                    </Typography>
                                    {b.employeeEmail && (
                                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                            {b.employeeEmail}
                                        </Typography>
                                    )}
                                    <Typography variant='caption' sx={{ display: 'block', color: birthdayColor.text, fontWeight: 500, mt: 0.25 }}>
                                        🎉 Click to celebrate!
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Work Anniversaries */}
            {hasAnniversaries && (
                <Box sx={{ mb: leaves.length > 0 ? 2.5 : 0 }}>
                    {(hasAnyAbove || hasBirthdays) && <Divider sx={{ mb: 2 }} />}
                    <Typography
                        variant='overline'
                        sx={{ fontWeight: 700, letterSpacing: 1.2, color: anniversaryColor.text, display: 'block', mb: 1 }}
                    >
                        🎊 Work Anniversaries
                    </Typography>
                    <Stack spacing={1}>
                        {anniversaries.map((a) => (
                            <Box
                                key={`an-${a.employeeEmail}`}
                                onClick={() => onCelebrate?.({
                                    type: 'anniversary',
                                    name: a.employeeName || a.employeeEmail,
                                    yearsOfService: a.yearsOfService,
                                })}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: 2,
                                    background: anniversaryColor.bg,
                                    border: `1px solid ${anniversaryColor.border}`,
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.15s, transform 0.15s',
                                    '&:hover': {
                                        boxShadow: `0 4px 16px ${anniversaryColor.border}55`,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                <Box sx={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>🎊</Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant='body2' sx={{ fontWeight: 700, color: anniversaryColor.text }}>
                                        {a.employeeName || a.employeeEmail}
                                    </Typography>
                                    {a.employeeEmail && (
                                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                            {a.employeeEmail}
                                        </Typography>
                                    )}
                                    {a.yearsOfService != null && (
                                        <Typography variant='caption' sx={{ display: 'block', color: anniversaryColor.text, fontWeight: 500, mt: 0.25 }}>
                                            {a.yearsOfService} {a.yearsOfService === 1 ? 'year' : 'years'} of service
                                        </Typography>
                                    )}
                                    <Typography variant='caption' sx={{ display: 'block', color: anniversaryColor.text, fontWeight: 500, mt: 0.25 }}>
                                        🎉 Click to celebrate!
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Leaves */}
            {leaves.length > 0 && (
                <Box>
                    {(hasAnyAbove || hasBirthdays || hasAnniversaries) && <Divider sx={{ mb: 2 }} />}
                    <Typography
                        variant='overline'
                        sx={{ fontWeight: 700, letterSpacing: 1.2, color: 'text.secondary', display: 'block', mb: 1 }}
                    >
                        On Leave — {leaves.length} {leaves.length === 1 ? 'person' : 'people'}
                    </Typography>
                    <Stack spacing={1.5}>
                        {leaves.map((l) => {
                            const fullName = l.fullName || l.employeeName || l.employeeEmail || '';
                            const category = l.categoryName || l.leaveType || '';
                            const c = getLeaveTypeColor(l, isDark);
                            return (
                                <Box
                                    key={l.id || `${l.leaveId}-${l.employeeEmail}`}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderLeft: `3px solid ${c.dot || c.border}`,
                                        borderTop: `1.5px solid ${c.border}`,
                                        borderRight: `1.5px solid ${c.border}`,
                                        borderBottom: `1.5px solid ${c.border}`,
                                        background: c.bg
                                    }}
                                >
                                    {/* Info */}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.25 }}>
                                            <Typography variant='body2' sx={{ fontWeight: 700, color: c.text }}>
                                                {fullName}
                                            </Typography>
                                            {category && (
                                                <Chip
                                                    size='small'
                                                    label={category}
                                                    sx={{
                                                        bgcolor: c.dot || c.border,
                                                        color: isDark ? '#1A1A2E' : '#fff',
                                                        fontWeight: 700,
                                                        fontSize: 10,
                                                        height: 20,
                                                        flexShrink: 0,
                                                        letterSpacing: 0.3
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        {l.employeeEmail && (
                                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                                {l.employeeEmail}
                                            </Typography>
                                        )}
                                        <Typography variant='caption' sx={{ display: 'block', mt: 0.5, color: c.text, fontWeight: 500 }}>
                                            {dayjs(l.startDate).format('MMM D')} – {dayjs(l.endDate).format('MMM D')}
                                            {l.totalNumberOfDays
                                                ? ` · ${l.totalNumberOfDays} day${l.totalNumberOfDays > 1 ? 's' : ''}`
                                                : ''}
                                        </Typography>
                                        {l.reason && (
                                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                                "{l.reason}"
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

// ---------- Celebration overlay (confetti + name card) -----------------------

const BIRTHDAY_CONFETTI_COLORS = [
    '#FF69B4', '#FFB6C1', '#FF1493', '#FFD700', '#FF6347',
    '#DA70D6', '#BA55D3', '#FF4500', '#FFA07A', '#FF85C2'
];
const ANNIVERSARY_CONFETTI_COLORS = [
    '#FFD700', '#FFC200', '#DAA520', '#C0C0C0', '#B8860B',
    '#E8C84A', '#F5D05E', '#BFA100', '#FFFACD', '#FFF8DC'
];

const BIRTHDAY_WISHES = [
    "May your day be as bright as your smile!",
    "Wishing you a day full of laughter and joy!",
    "Another year wiser, another year more awesome!",
    "May all your birthday wishes come true!",
    "Here's to a fantastic year ahead!",
    "Celebrating the amazing person you are!",
    "Hope your day is as special as you are!",
    "Sending you the biggest birthday hugs!",
    "May this year bring you everything you dream of!",
    "Another trip around the sun — you're killing it!",
    "Wishing you a year filled with love and laughter!",
    "Today is YOUR day — make it legendary!",
    "May your birthday be the start of an incredible year!",
    "Cheers to you and the amazing human you've become!",
    "Wishing you all the happiness in the world today!",
    "You deserve all the good things life has to offer!",
    "May your birthday sparkle with joy and cheer!",
    "Here's to new adventures in your new year!",
    "May your day be filled with good vibes only!",
    "Wishing you health, happiness, and endless success!",
    "Shine bright today — it's all about you!",
    "May this year be your best chapter yet!",
    "You make the world a better place just by being in it!",
    "Wishing you a day as wonderful as you are!",
    "May the birthday magic never fade!",
    "Here's to the amazing journey ahead of you!",
    "Blessed to have you in our team — happy birthday!",
    "May joy follow you everywhere today!",
    "Today the world is a little brighter because it's your birthday!",
    "Wishing you a day filled with cake, laughter, and love!",
    "May your dreams take flight this year!",
    "Celebrating you and your awesomeness today!",
    "Life gets better with every year — enjoy every moment!",
    "Wishing you the kind of day that makes memories!",
    "May happiness be your constant companion!",
    "Here's to another year of being absolutely brilliant!",
    "You are a gift to everyone around you — happy birthday!",
    "May every moment of your birthday be pure bliss!",
    "Sending birthday love your way today!",
    "May your heart be full of joy today and always!",
    "Another year, another chance to be incredible!",
    "Wishing you endless smiles and big surprises!",
    "May this birthday be the start of something wonderful!",
    "Here's to the legend that is you — happy birthday!",
    "You deserve the most magical birthday ever!",
    "May your birthday be wrapped in happiness!",
    "Wishing you nothing but pure happiness today!",
    "May your special day shine as bright as you do!",
    "Cheers to another year of greatness!",
    "May today bring you all the joy you've given others!",
    "Wishing you a birthday as fabulous as you are!",
    "Here's to more adventures, more laughs, more YOU!",
    "May your birthday glow with warmth and love!",
    "Today is the perfect day to celebrate YOU!",
    "Wishing you sky-high happiness on your birthday!",
    "May this year be full of wonderful surprises!",
    "Here's to a birthday that's truly unforgettable!",
    "May your candles shine as bright as your future!",
    "Wishing you a day drenched in happiness!",
    "You are one in a million — happy birthday!",
    "May every wish you make today come true!",
    "Here's to the incredible story you're still writing!",
    "Wishing you a birthday overflowing with joy!",
    "May today be the beginning of a perfect year!",
    "Celebrating the wonder that is you today!",
    "May good things rain down on you all day long!",
    "Wishing you a birthday to remember forever!",
    "Here's to you — bold, brilliant, and unstoppable!",
    "May happiness wrap around you today like a big warm hug!",
    "Wishing you a birthday as bright as the stars!",
    "You've made this team brighter — happy birthday!",
    "May every second of your birthday feel like magic!",
    "Here's to another year of amazing memories ahead!",
    "Wishing you a birthday full of everything you love!",
    "May your birthday be the best one yet!",
    "You are celebrated today and every day!",
    "Wishing you laughter, love, and pure happiness today!",
    "May your special day be absolutely perfect!",
    "Here's to YOU — the reason today is special!",
    "Wishing you a birthday that lights up your soul!",
    "May this year bring you tremendous joy and success!",
    "Celebrate today — you've earned every bit of it!",
    "May your birthday be full of golden moments!",
    "Wishing you a day that's totally YOU!",
    "Here's to a birthday as warm as your spirit!",
    "May joy be your faithful companion today and always!",
    "Wishing you a birthday more beautiful than a sunrise!",
    "May your birthday be the beginning of pure magic!",
    "Here's to the beautiful soul that you are!",
    "Wishing you an epic birthday celebration!",
    "May your birthday be everything you've imagined!",
    "Another year older, another year more amazing!",
    "May your birthday be nothing short of spectacular!",
    "Wishing you a day full of blessings and brightness!",
    "Here's to more chapters in your incredible story!",
    "May this birthday lead to your greatest year yet!",
    "Wishing you a birthday as radiant as your personality!",
    "May every smile today be a reflection of your joy!",
    "Celebrating the wonder you bring to this world!",
    "Wishing you a birthday full of warm hugs and big dreams!",
    "May this special day remind you how loved you are!",
    "Here's to a birthday that matches your fabulousness!",
    "Wishing you a lifetime of moments worth celebrating!",
    "May today's joy be just a preview of the year ahead!",
    "Here's to the star you've always been — shine on!",
    "Wishing you a birthday beyond your wildest dreams!",
    "May your birthday be the spark for an incredible year!",
    "Cheers to the amazing human who deserves it all!",
    "Wishing you a birthday wrapped in sunshine and joy!",
    "May your day be perfect in every single way!",
    "Here's to celebrating you with all the love!",
    "Wishing you a birthday full of sweet surprises!",
    "May your birthday be as magical as you make life for others!",
    "Here's to another year of friendship and laughter together!",
    "Wishing you a sparkling birthday and a glittering year ahead!",
    "May the stars align perfectly for you today!",
    "Here's to you — the most wonderful person today!",
    "Wishing you a birthday so good it rivals a fairytale!",
    "May today fill your heart with memories to treasure!",
    "Celebrating you, the bright light in our lives!",
    "May your birthday bring you boundless happiness!",
    "Wishing you a day that shines from start to finish!",
    "Here's to a birthday chapter that's truly extraordinary!",
    "May your birthday be the highlight of a magnificent year!",
    "Wishing you the sweetest, most joyful birthday ever!",
    "May you feel the love and warmth from everyone around you!",
    "Here's to you and the beautiful path you're on!",
    "Wishing you a birthday as grand as your dreams!",
    "May your special day be everything your heart desires!",
    "Celebrating the unique and extraordinary person you are!",
    "Wishing you a birthday that makes the whole world smile!",
    "May today mark the start of your most joyful year!",
    "Here's to a birthday that sets your heart on fire!",
    "Wishing you joy that's impossible to contain today!",
    "May your birthday be a perfect masterpiece of joy!",
    "Cheers to the incredible journey you're on!",
    "Wishing you a birthday that echoes in your heart forever!",
    "May every moment today be a treasure worth keeping!",
    "Here's to a birthday that belongs entirely to you!",
    "Wishing you skies full of happiness and a heart full of love!",
    "May today be the beginning of your greatest adventure!",
    "Celebrating the gift that is you — happy birthday!",
    "Wishing you a birthday that's truly worth remembering!",
    "May your birthday glow with the light of your spirit!",
    "Here's to a birthday as magnificent as you deserve!",
    "Wishing you a day that feels like forever summer!",
    "May your birthday be the happiest day of your year!",
    "Here's to you — keep shining, keep glowing, keep growing!",
    "Wishing you a birthday filled with blessings and bliss!",
    "May today remind you of how cherished you truly are!",
    "Here's to a birthday worth singing about!",
    "Wishing you a birthday that sets the tone for a brilliant year!",
    "May your birthday be the gift that keeps on giving!",
    "Celebrating you and all the light you bring to this team!",
    "Wishing you the most heartfelt and joyful birthday!",
    "May this birthday open doors to new and exciting adventures!",
    "Here's to the day that honors the amazing you!",
    "Wishing you a birthday bigger than all your dreams!",
    "May your laughter fill every corner of this day!",
    "Here's to a birthday as vibrant and alive as you are!",
    "Wishing you a beautiful birthday and a spectacular year!",
    "May today remind you that the best is always yet to come!",
    "Here's to a birthday that inspires your brightest chapter!",
    "Wishing you a birthday that warms the heart and lifts the spirit!",
    "May your special day be wrapped in love from all of us!",
    "Here's to another 365 days of being absolutely wonderful!",
    "Wishing you joy, peace, love, and a whole lot of cake today!",
    "May your birthday be the spark that lights your best year yet!",
    "Celebrating you today, tomorrow, and always!",
    "Wishing you a birthday as phenomenal as your spirit!",
    "May every birthday bring you closer to your dreams!",
    "Here's to you — endlessly appreciated and deeply celebrated!",
    "Wishing you a birthday full of laughter-filled memories!",
    "May today be the perfect prelude to an extraordinary year!",
    "Here's to a birthday so wonderful it leaves you speechless!",
    "Wishing you a birthday that shines through every year to come!",
    "May the joy of today carry you through the whole year!",
    "Here's to a birthday as special as the person celebrating it!",
    "Wishing you the most brilliant birthday of all time!",
];

const ANNIVERSARY_WISHES = [
    "Thank you for your incredible dedication and loyalty!",
    "Your commitment to this team is truly inspiring!",
    "Another year of making a real difference — congratulations!",
    "Your journey here has been nothing short of extraordinary!",
    "Here's to the amazing contribution you make every day!",
    "Your dedication is the foundation of our success!",
    "Congratulations on another year of excellence!",
    "We are so grateful to have you on this team!",
    "Your work anniversary is a celebration of your incredible impact!",
    "Thank you for being such a cornerstone of our team!",
    "Celebrating the talent and heart you bring every single day!",
    "Your loyalty and hard work are truly priceless — thank you!",
    "Another milestone in an already amazing career!",
    "We celebrate you today for all that you do and all that you are!",
    "Your contribution to this team is immeasurable — congratulations!",
    "Thank you for choosing to grow with us year after year!",
    "Your dedication inspires everyone around you — keep shining!",
    "Congratulations on another year of incredible achievements!",
    "We're so lucky to have someone as brilliant as you!",
    "Your passion and commitment make every day better here!",
    "Here's to many more years of growth and success together!",
    "Thank you for your unwavering commitment and hard work!",
    "Celebrating a journey that has made us all better!",
    "Your work anniversary is a reminder of how truly valued you are!",
    "Congratulations on reaching this wonderful milestone!",
    "Your impact on this team will be felt for years to come!",
    "We honor and celebrate your loyalty today and every day!",
    "Thank you for the energy and excellence you bring to work!",
    "Another year down — and every year better than the last!",
    "Your dedication to this company is something we never take for granted!",
    "Celebrating the incredible mark you've left on this team!",
    "Here's to another year of shared victories and great memories!",
    "Your work anniversary is a milestone worth celebrating grandly!",
    "Thank you for being the backbone of everything we achieve together!",
    "Congratulations on yet another year of outstanding service!",
    "Your commitment speaks louder than words — thank you!",
    "We're honored to have had you with us for another year!",
    "Here's to the journey we've shared and the road ahead!",
    "Your talent and dedication are a gift to this entire team!",
    "Congratulations on this well-deserved milestone!",
    "You've made your mark here in the most beautiful way!",
    "Thank you for the passion you pour into everything you do!",
    "Celebrating another year of your amazing contribution!",
    "Your loyalty is a treasure we will always be grateful for!",
    "Here's to the incredible professional you've become here!",
    "Thank you for your resilience and dedication year after year!",
    "Another anniversary, another reminder of how lucky we are to have you!",
    "Your hard work has shaped this team in ways beyond measure!",
    "Congratulations on this fantastic work anniversary milestone!",
    "We celebrate you today for your heart, your talent, and your spirit!",
    "Thank you for being a true professional through every challenge!",
    "Another year, another reason to say — we're so glad you're here!",
    "Your dedication to excellence never goes unnoticed — congratulations!",
    "Here's to a lifetime of milestones still ahead of you!",
    "Thank you for the consistency, passion, and brilliance you bring!",
    "Celebrating the journey that's made us stronger together!",
    "Your work anniversary is a reminder of the excellence you represent!",
    "Congratulations on being an irreplaceable part of this team!",
    "We're proud of everything you've accomplished and who you've become!",
    "Thank you for showing up with heart and hustle every single day!",
    "Another year wiser, stronger, and more incredible than before!",
    "Your work anniversary is a celebration of impact and legacy!",
    "Congratulations on building something truly meaningful here!",
    "We celebrate the dedication that sets you apart every day!",
    "Thank you for your loyalty and your amazing contributions!",
    "Here's to the mark you've made and the future still ahead!",
    "Your commitment to excellence is something truly worth celebrating!",
    "Another year of being absolutely essential to what we do!",
    "Congratulations on this beautiful chapter of your career journey!",
    "We honor your resilience, your growth, and your loyalty today!",
    "Thank you for being someone we can always count on!",
    "Your work anniversary reminds us why we are grateful every day!",
    "Celebrating you and the extraordinary difference you make!",
    "Here's to the years behind you and the bright future ahead!",
    "Another year of incredible work — congratulations!",
    "Your dedication has been the driving force behind so much success!",
    "Thank you for choosing this team and growing with us!",
    "Congratulations on reaching this magnificent milestone!",
    "We celebrate the trust, hard work, and passion you bring daily!",
    "Your work anniversary is a testament to your extraordinary character!",
    "Here's to the legend in the making that you truly are!",
    "Thank you for being a shining example of dedication and excellence!",
    "Another year of memories, milestones, and magnificent achievements!",
    "Congratulations on this remarkable work anniversary!",
    "Your commitment to doing great work inspires everyone here!",
    "We're so proud to have you as a part of our story!",
    "Thank you for all the brilliance you bring to work every day!",
    "Another year of being truly and completely exceptional!",
    "Congratulations on your work anniversary — you've earned every applause!",
    "We celebrate the passion that fuels everything you do!",
    "Your loyalty and talent are the heart of what makes this team great!",
    "Here's to the incredible journey you've been on with us!",
    "Thank you for giving your best, every single day, without fail!",
    "Congratulations on an amazing year of growth and contribution!",
    "We celebrate the positive energy you bring into our workplace!",
    "Another year of excellence — and we can't wait to see what's next!",
    "Your work anniversary reminds us that great things take time!",
    "Thank you for the extraordinary commitment you've shown us!",
    "Celebrating the talent, the heart, and the hustle that is you!",
    "Congratulations on this landmark year in your journey with us!",
    "Here's to the friendship and the work ethic that define you!",
    "Another year stronger, smarter, and more inspiring than ever!",
    "Thank you for going above and beyond in everything you do!",
    "Your work anniversary is a celebration of lasting excellence!",
    "Congratulations on being one of the finest people on this team!",
    "We celebrate the trust you've placed in us by staying with us!",
    "Your dedication is the compass that guides this team forward!",
    "Here's to another year of growing and succeeding together!",
    "Thank you for being the standard bearer of excellence here!",
    "Another year of making history — congratulations on your anniversary!",
    "Celebrating the passion and professionalism that you embody daily!",
    "Your journey with this team has been nothing short of brilliant!",
    "Congratulations on this momentous milestone — you've earned it!",
    "We celebrate the unwavering spirit you bring to all you do!",
    "Thank you for being a constant source of strength for this team!",
    "Your work anniversary marks another year of meaningful impact!",
    "Here's to the incredible work you do and the person you are!",
    "Another year of pushing boundaries and setting new standards!",
    "Congratulations on this wonderful milestone in your career!",
    "We celebrate you and the legacy you're building here!",
    "Thank you for your heart, your courage, and your excellence!",
    "Your work anniversary is a celebration of all you stand for!",
    "Here's to the amazing things still ahead in your journey!",
    "Another year of being completely irreplaceable — congratulations!",
    "Celebrating the incredible chapters you've added to our story!",
    "Thank you for your commitment to growing and giving your best!",
    "Your work anniversary is a moment to pause and honor you fully!",
    "Congratulations on being the kind of person who makes a difference!",
    "We celebrate the courage, the competence, and the care you bring!",
    "Another year of excellence — the team is better because of you!",
    "Thank you for being a true professional in every sense of the word!",
    "Your work anniversary is proof that great things come with dedication!",
    "Here's to the unbreakable spirit that carries you forward!",
    "Congratulations on a year that reflects your incredible character!",
    "We celebrate the resilience, wisdom, and heart you show every day!",
    "Another year of being an absolute legend on this team!",
    "Thank you for the integrity and passion you bring to your work!",
    "Your work anniversary is a milestone that deserves every cheer!",
    "Celebrating the profound and lasting difference you've made here!",
    "Congratulations on another year of being truly extraordinary!",
    "We celebrate you today — the heart of what makes this team great!",
    "Another year of leaving things better than you found them!",
    "Thank you for being the kind of professional everyone aspires to be!",
    "Your work anniversary is a celebration of your incredible journey!",
    "Here's to a future as bright and bold as the work you've already done!",
    "Congratulations on this beautiful and well-deserved milestone!",
    "We honor your loyalty, your brilliance, and your enduring spirit!",
    "Another year of incredible service — thank you from all of us!",
    "Your work anniversary is a tribute to your extraordinary commitment!",
    "Celebrating you with all the gratitude and admiration you deserve!",
    "Thank you for believing in this team and growing with it together!",
    "Your work anniversary is a testament to your enduring excellence!",
    "Congratulations on being an extraordinary member of an extraordinary team!",
    "Here's to the years you've poured your heart into this work!",
    "Another year of being the gold standard on this team!",
    "We celebrate you today with tremendous respect and deep gratitude!",
    "Thank you for every sacrifice, every effort, and every breakthrough!",
    "Your work anniversary reminds us of all the ways you've shaped us!",
    "Congratulations on reaching a milestone that truly means something!",
    "We celebrate the journey, the growth, and the wonderful person you are!",
    "Another year of making excellence look effortless — congratulations!",
    "Thank you for the endless value you bring to everything you do!",
    "Your work anniversary is a day to celebrate how truly valued you are!",
    "Here's to a milestone that's just the beginning of even greater things!",
    "Congratulations on another year of outstanding dedication!",
    "We celebrate the amazing work and wonderful spirit you bring daily!",
    "Thank you for making this workplace a better place every single day!",
    "Your work anniversary is a shining reminder of your beautiful legacy!",
    "Another year of passion, purpose, and professionalism — congratulations!",
    "Celebrating the heart and soul you pour into everything here!",
    "Congratulations on this incredible milestone — you've made us all proud!",
    "We honor your journey, your growth, and your remarkable dedication!",
    "Thank you for the years of trust, excellence, and unwavering loyalty!",
    "Your work anniversary is a celebration of everything you stand for!",
    "Here's to the brilliant years ahead and all the milestones still to come!",
];

const getRandomWish = (wishes) => wishes[Math.floor(Math.random() * wishes.length)];

const runBirthdayConfetti = (canvasRef, layer = 'back') => {
    if (!canvasRef.current) return;
    const isFront = layer === 'front';
    const myConfetti = confetti.create(canvasRef.current, { resize: true, useWorker: true });

    myConfetti({
        particleCount: isFront ? 90 : 140,
        angle: 90,
        spread: isFront ? 95 : 120,
        origin: { x: 0.5, y: isFront ? 0.52 : 0.6 },
        colors: BIRTHDAY_CONFETTI_COLORS,
        startVelocity: isFront ? 42 : 50,
        gravity: isFront ? 0.72 : 0.8,
        ticks: isFront ? 280 : 350,
        scalar: isFront ? 0.95 : 1.1,
    });
    setTimeout(() => {
        myConfetti({
            particleCount: isFront ? 45 : 70, angle: 60, spread: isFront ? 64 : 80,
            origin: { x: 0, y: isFront ? 0.6 : 0.7 },
            colors: BIRTHDAY_CONFETTI_COLORS, startVelocity: isFront ? 38 : 45, ticks: isFront ? 220 : 280,
        });
        myConfetti({
            particleCount: isFront ? 45 : 70, angle: 120, spread: isFront ? 64 : 80,
            origin: { x: 1, y: isFront ? 0.6 : 0.7 },
            colors: BIRTHDAY_CONFETTI_COLORS, startVelocity: isFront ? 38 : 45, ticks: isFront ? 220 : 280,
        });
    }, isFront ? 120 : 200);
    setTimeout(() => {
        myConfetti({
            particleCount: isFront ? 36 : 60, spread: 360, origin: { x: 0.5, y: isFront ? 0.32 : 0.4 },
            colors: BIRTHDAY_CONFETTI_COLORS, gravity: 0.4, ticks: isFront ? 160 : 200, scalar: isFront ? 0.6 : 0.7,
        });
    }, isFront ? 380 : 600);
};

const runAnniversaryConfetti = (canvasRef, layer = 'back') => {
    if (!canvasRef.current) return;
    const isFront = layer === 'front';
    const myConfetti = confetti.create(canvasRef.current, { resize: true, useWorker: true });

    myConfetti({
        particleCount: isFront ? 70 : 100,
        angle: 90,
        spread: isFront ? 48 : 60,
        origin: { x: 0.5, y: isFront ? 0.48 : 0.55 },
        colors: ANNIVERSARY_CONFETTI_COLORS,
        startVelocity: isFront ? 48 : 60,
        gravity: isFront ? 0.52 : 0.6,
        ticks: isFront ? 300 : 400,
        shapes: ['star'],
        scalar: isFront ? 1.15 : 1.4,
    });
    setTimeout(() => {
        myConfetti({
            particleCount: isFront ? 55 : 80, angle: 90, spread: isFront ? 140 : 160,
            origin: { x: 0.5, y: 0 },
            colors: ANNIVERSARY_CONFETTI_COLORS, startVelocity: isFront ? 18 : 20,
            gravity: 0.5, ticks: isFront ? 360 : 500, shapes: ['star'], scalar: isFront ? 1.05 : 1.2,
        });
    }, isFront ? 220 : 350);
    setTimeout(() => {
        myConfetti({
            particleCount: isFront ? 35 : 50, angle: 70, spread: isFront ? 40 : 50,
            origin: { x: 0, y: isFront ? 0.56 : 0.65 },
            colors: ANNIVERSARY_CONFETTI_COLORS, startVelocity: isFront ? 32 : 40,
            ticks: isFront ? 220 : 300, shapes: ['star'], scalar: isFront ? 1.05 : 1.3,
        });
        myConfetti({
            particleCount: isFront ? 35 : 50, angle: 110, spread: isFront ? 40 : 50,
            origin: { x: 1, y: isFront ? 0.56 : 0.65 },
            colors: ANNIVERSARY_CONFETTI_COLORS, startVelocity: isFront ? 32 : 40,
            ticks: isFront ? 220 : 300, shapes: ['star'], scalar: isFront ? 1.05 : 1.3,
        });
    }, isFront ? 100 : 150);
};

// SVG Illustrations
const BirthdaySVG = () => (
    <svg viewBox="0 0 220 200" width="220" height="200" fill="none" xmlns="http://www.w3.org/2000/svg"
        style={{ filter: 'drop-shadow(0 8px 32px rgba(255,105,180,0.45))' }}>
        {/* Balloons */}
        {/* Left balloon */}
        <ellipse cx="55" cy="72" rx="28" ry="34" fill="#FF69B4" opacity="0.92"/>
        <ellipse cx="49" cy="62" rx="9" ry="6" fill="#fff" opacity="0.28"/>
        <path d="M55 106 Q52 115 56 130" stroke="#FF69B4" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <polygon points="53,106 57,106 55,112" fill="#FF69B4"/>
        {/* Center balloon */}
        <ellipse cx="110" cy="60" rx="32" ry="40" fill="#FF1493" opacity="0.95"/>
        <ellipse cx="102" cy="48" rx="10" ry="7" fill="#fff" opacity="0.28"/>
        <path d="M110 100 Q107 112 111 132" stroke="#FF1493" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <polygon points="108,100 112,100 110,107" fill="#FF1493"/>
        {/* Right balloon */}
        <ellipse cx="165" cy="72" rx="28" ry="34" fill="#DA70D6" opacity="0.92"/>
        <ellipse cx="159" cy="62" rx="9" ry="6" fill="#fff" opacity="0.28"/>
        <path d="M165 106 Q162 115 166 130" stroke="#DA70D6" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
        <polygon points="163,106 167,106 165,112" fill="#DA70D6"/>
        {/* Cake */}
        <rect x="70" y="145" width="80" height="36" rx="8" fill="#FFB6C1"/>
        <rect x="70" y="130" width="80" height="22" rx="6" fill="#FF85C2"/>
        {/* Frosting drips */}
        <path d="M70 135 Q78 142 86 135 Q94 128 102 135 Q110 142 118 135 Q126 128 134 135 Q142 142 150 135" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round"/>
        {/* Candles */}
        <rect x="96" y="118" width="7" height="16" rx="3" fill="#FFD700"/>
        <rect x="113" y="118" width="7" height="16" rx="3" fill="#FF6347"/>
        {/* Flames */}
        <ellipse cx="99.5" cy="116" rx="3.5" ry="5" fill="#FFA500" opacity="0.9"/>
        <ellipse cx="99.5" cy="115" rx="2" ry="3" fill="#FFD700"/>
        <ellipse cx="116.5" cy="116" rx="3.5" ry="5" fill="#FFA500" opacity="0.9"/>
        <ellipse cx="116.5" cy="115" rx="2" ry="3" fill="#FFD700"/>
        {/* Stars */}
        <text x="28" y="38" fontSize="18" fill="#FFD700" opacity="0.85">✦</text>
        <text x="178" y="32" fontSize="14" fill="#FF85C2" opacity="0.85">✦</text>
        <text x="15" y="110" fontSize="12" fill="#DA70D6" opacity="0.7">✦</text>
        <text x="190" y="100" fontSize="20" fill="#FFD700" opacity="0.75">✦</text>
    </svg>
);

const AnniversarySVG = () => (
    <svg viewBox="0 0 220 200" width="220" height="200" fill="none" xmlns="http://www.w3.org/2000/svg"
        style={{ filter: 'drop-shadow(0 8px 32px rgba(255,215,0,0.55))' }}>
        {/* Trophy cup */}
        <path d="M76 155 Q76 170 110 170 Q144 170 144 155" stroke="#DAA520" strokeWidth="3" fill="#FFD700" opacity="0.2"/>
        <rect x="98" y="155" width="24" height="14" rx="4" fill="#DAA520"/>
        <rect x="86" y="168" width="48" height="8" rx="4" fill="#C0C0C0"/>
        {/* Cup body */}
        <path d="M76 80 Q72 100 80 120 Q88 138 110 142 Q132 138 140 120 Q148 100 144 80 Z" fill="#FFD700"/>
        <path d="M76 80 Q72 100 80 120 Q88 138 110 142 Q132 138 140 120 Q148 100 144 80 Z"
            fill="url(#goldGrad)" opacity="0.6"/>
        <defs>
            <linearGradient id="goldGrad" x1="76" y1="80" x2="144" y2="142" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFF8DC"/>
                <stop offset="1" stopColor="#B8860B"/>
            </linearGradient>
        </defs>
        {/* Handles */}
        <path d="M76 88 Q56 96 60 116 Q62 128 76 128" stroke="#DAA520" strokeWidth="5" fill="none" strokeLinecap="round"/>
        <path d="M144 88 Q164 96 160 116 Q158 128 144 128" stroke="#DAA520" strokeWidth="5" fill="none" strokeLinecap="round"/>
        {/* Rim */}
        <rect x="73" y="78" width="74" height="10" rx="5" fill="#FFE566"/>
        {/* Star on trophy */}
        <text x="97" y="122" fontSize="24" fill="#B8860B" fontWeight="bold">★</text>
        {/* Sparkles / Stars around */}
        <text x="22" y="60" fontSize="22" fill="#FFD700" opacity="0.9">★</text>
        <text x="180" y="55" fontSize="18" fill="#FFC200" opacity="0.85">★</text>
        <text x="30" y="140" fontSize="15" fill="#DAA520" opacity="0.75">★</text>
        <text x="185" y="138" fontSize="24" fill="#FFD700" opacity="0.8">★</text>
        <text x="100" y="42" fontSize="16" fill="#FFC200" opacity="0.8">★</text>
        <text x="145" y="165" fontSize="12" fill="#E8C84A" opacity="0.7">✦</text>
        {/* Confetti dots */}
        <circle cx="48" cy="95" r="4" fill="#FF69B4" opacity="0.7"/>
        <circle cx="172" cy="90" r="4" fill="#FF69B4" opacity="0.7"/>
        <circle cx="42" cy="170" r="3" fill="#DA70D6" opacity="0.6"/>
        <circle cx="178" cy="168" r="3" fill="#FFD700" opacity="0.8"/>
    </svg>
);

const CelebrationOverlay = ({ celebration, onClose }) => {
    const backCanvasRef = useRef(null);
    const frontCanvasRef = useRef(null);
    const { type, name, yearsOfService } = celebration;
    const isBirthday = type === 'birthday';

    // Pick a random wish once when overlay mounts
    const wish = useMemo(
        () => getRandomWish(isBirthday ? BIRTHDAY_WISHES : ANNIVERSARY_WISHES),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        if (!backCanvasRef.current || !frontCanvasRef.current) return;
        const raf = requestAnimationFrame(() => {
            if (isBirthday) {
                runBirthdayConfetti(backCanvasRef, 'back');
                runBirthdayConfetti(frontCanvasRef, 'front');
            } else {
                runAnniversaryConfetti(backCanvasRef, 'back');
                runAnniversaryConfetti(frontCanvasRef, 'front');
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [isBirthday]);

    const label = isBirthday ? 'Happy Birthday!' : 'Happy Work Anniversary!';
    const accentColor = isBirthday ? '#FF1493' : '#DAA520';
    const subColor = isBirthday ? '#FF85C2' : '#FFD700';

    return (
        <Box
            onClick={onClose}
            sx={{
                position: 'fixed', inset: 0, zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isBirthday
                    ? 'rgba(60, 0, 40, 0.55)'
                    : 'rgba(40, 30, 0, 0.55)',
                backdropFilter: 'blur(6px)',
                cursor: 'pointer',
            }}
        >
            {/* Background confetti canvas */}
            <canvas
                ref={backCanvasRef}
                style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />

            {/* Centre card */}
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: 'relative', zIndex: 1,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 1.5,
                    px: 5, py: 4,
                    borderRadius: 6,
                    background: isBirthday
                        ? 'linear-gradient(145deg, rgba(255,105,180,0.18) 0%, rgba(180,0,100,0.22) 100%)'
                        : 'linear-gradient(145deg, rgba(255,215,0,0.18) 0%, rgba(160,120,0,0.22) 100%)',
                    backdropFilter: 'blur(24px)',
                    boxShadow: isBirthday
                        ? '0 8px 48px rgba(255,20,147,0.35), 0 1.5px 0 rgba(255,255,255,0.18) inset'
                        : '0 8px 48px rgba(218,165,32,0.4), 0 1.5px 0 rgba(255,255,255,0.18) inset',
                    border: `1.5px solid ${accentColor}55`,
                    maxWidth: 440, textAlign: 'center',
                    animation: 'celebPop 0.45s cubic-bezier(0.34,1.56,0.64,1)',
                    '@keyframes celebPop': {
                        from: { opacity: 0, transform: 'scale(0.55) translateY(30px)' },
                        to: { opacity: 1, transform: 'scale(1) translateY(0)' },
                    },
                }}
            >
                {/* SVG illustration */}
                {isBirthday ? <BirthdaySVG /> : <AnniversarySVG />}

                {/* Label */}
                <Typography
                    sx={{
                        fontSize: 13, fontWeight: 700, letterSpacing: 4,
                        textTransform: 'uppercase', color: subColor,
                        mt: -1,
                    }}
                >
                    {label}
                </Typography>

                {/* Name */}
                <Typography
                    variant='h4'
                    sx={{
                        fontWeight: 900, color: '#fff',
                        textShadow: `0 2px 20px ${accentColor}99`,
                        letterSpacing: 0.5, lineHeight: 1.2,
                    }}
                >
                    {name}
                </Typography>

                {!isBirthday && yearsOfService != null && (
                    <Typography
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: 'rgba(255,255,255,0.14)',
                            border: '1px solid rgba(255,255,255,0.22)',
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 700,
                            letterSpacing: 0.3,
                        }}
                    >
                        Celebrating {yearsOfService} {yearsOfService === 1 ? 'year' : 'years'}
                    </Typography>
                )}

                {/* Wish */}
                <Typography
                    sx={{
                        fontSize: 14.5, color: 'rgba(255,255,255,0.82)',
                        fontStyle: 'italic', lineHeight: 1.55,
                        maxWidth: 360, mt: 0.5,
                    }}
                >
                    "{wish}"
                </Typography>
            </Box>

            {/* Foreground confetti canvas */}
            <canvas
                ref={frontCanvasRef}
                style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    pointerEvents: 'none',
                    zIndex: 2,
                }}
            />

            {/* Close button */}
            <IconButton
                onClick={onClose}
                size='small'
                sx={{
                    position: 'absolute', top: 16, right: 16, zIndex: 3,
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,0.30)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                }}
            >
                <CloseIcon />
            </IconButton>
        </Box>
    );
};

// ---------- Main component ----------------------------------------------------

const LeaveCalendarView = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const legendItems = useMemo(
        () =>
            LEAVE_TYPE_ORDER.map((label) => ({
                label,
                color: (isDark ? DARK_LEAVE_TYPE_COLOR_MAP : LEAVE_TYPE_COLOR_MAP)[label]
            })),
        [isDark]
    );

    const [monthDate, setMonthDate] = useState(dayjs().startOf('month'));
    const [requestMonthDate, setRequestMonthDate] = useState(dayjs().startOf('month'));
    // Status is fixed to Approved per product requirement.
    const status = DEFAULT_STATUS;

    const [calendars, setCalendars] = useState([]);
    const [isCalendarsLoading, setIsCalendarsLoading] = useState(true);
    const [calenderId, setCalenderId] = useState(null);

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [celebration, setCelebration] = useState(null); // { type: 'birthday'|'anniversary', name: string }
    const [selectedLeaveType, setSelectedLeaveType] = useState(null);

    const year = requestMonthDate.year();
    const month = requestMonthDate.month() + 1;

    // Load calendars + pre-select Bangladesh (or default / first)
    useEffect(() => {
        let cancelled = false;
        const loadCalendars = async () => {
            try {
                setIsCalendarsLoading(true);
                const res = await fetchRequest('/calender/get/all', 'GET');
                const calendarsList = Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.data)
                      ? res.data.data
                      : Array.isArray(res)
                        ? res
                        : [];
                const list = calendarsList.map((c) => ({
                    id: String(c.id),
                    name: c.name,
                    isDefault: !!c.isDefault
                }));
                if (cancelled) return;
                setCalendars(list);
                if (list.length > 0) {
                    const bd = list.find(
                        (c) =>
                            normalizeLabel(c.name) ===
                            normalizeLabel(DEFAULT_CALENDAR_NAME)
                    );
                    const def = list.find((c) => c.isDefault);
                    setCalenderId((bd || def || list[0]).id);
                }
            } catch (err) {
                if (!cancelled) setCalendars([]);
            } finally {
                if (!cancelled) setIsCalendarsLoading(false);
            }
        };
        loadCalendars();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (calendars.length === 0) return;
        const currentExists = calendars.some((c) => c.id === calenderId);
        if (currentExists) return;

        const bd = calendars.find(
            (c) =>
                normalizeLabel(c.name) ===
                normalizeLabel(DEFAULT_CALENDAR_NAME)
        );
        const def = calendars.find((c) => c.isDefault);
        setCalenderId((bd || def || calendars[0]).id);
    }, [calendars, calenderId]);

    useEffect(() => {
        if (monthDate.isSame(requestMonthDate, 'month')) return;

        const timeoutId = setTimeout(() => {
            setRequestMonthDate(monthDate);
        }, MONTH_FETCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [monthDate, requestMonthDate]);

    useEffect(() => {
        setRequestMonthDate(monthDate);
    }, [calenderId, status]);

    // Load calendar data
    useEffect(() => {
        if (isCalendarsLoading) return;
        if (calendars.length > 0 && !calenderId) return;

        let cancelled = false;
        const load = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const res = await fetchRequest('/leave/calendar', 'GET', {
                    year,
                    month,
                    status: status || undefined,
                    'calender-id': calenderId || undefined
                });
                if (cancelled) return;
                setData(res?.data ?? res ?? null);
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Failed to load calendar');
                    setData(null);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [year, month, status, calenderId, isCalendarsLoading, calendars.length]);

    const weekends = useMemo(() => {
        const raw = data?.weekends;
        if (!Array.isArray(raw) || raw.length === 0) return [0, 6];
        const normalized = raw
            .map((n) => Number(n))
            .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
        return normalized.length > 0 ? normalized : [0, 6];
    }, [data?.weekends]);

    const days = useMemo(() => data?.days || [], [data?.days]);

    // Build events for react-big-calendar from day data
    const allEvents = useMemo(() => {
        const leaveMap = new Map();
        const holidayMap = new Map();
        const birthdayMap = new Map();
        const anniversaryMap = new Map();
        for (const day of days) {
            for (const l of day.leaves || []) {
                const hasPartialSlice = getLeaveSliceFraction(l) < 1;
                const key = hasPartialSlice
                    ? `${day.date}-${l.leaveId != null ? String(l.leaveId) : `${l.employeeEmail}-${l.startDate}`}`
                    : l.leaveId != null
                        ? String(l.leaveId)
                        : `${l.employeeEmail}-${l.startDate}`;
                if (!leaveMap.has(key)) {
                    const name = l.fullName || l.employeeName || l.employeeEmail || '';
                    const colorKey = l.employeeEmail || name;
                    const sourceDate = day.date;
                    leaveMap.set(key, {
                        id: `leave-${key}`,
                        title: name,
                        start: toCalendarStartDate(hasPartialSlice ? day.date : (l.startDate || day.date)),
                        end: toCalendarEndDate(hasPartialSlice ? day.date : (l.endDate || day.date)),
                        allDay: true,
                        resource: {
                            type: 'leave',
                            colorKey,
                            sourceDate,
                            sliceFraction: getLeaveSliceFraction(l),
                            ...l
                        }
                    });
                }
            }
            for (const h of day.holidays || []) {
                const key = `${day.date}-${h.id}`;
                if (!holidayMap.has(key)) {
                    holidayMap.set(key, {
                        id: `holiday-${key}`,
                        title: h.name || h.title || '',
                        start: toCalendarStartDate(day.date),
                        end: toCalendarEndDate(day.date),
                        allDay: true,
                        resource: { type: 'holiday', sourceDate: day.date, ...h }
                    });
                }
            }
            for (const b of day.birthdays || []) {
                const key = `${day.date}-bd-${b.employeeEmail}`;
                if (!birthdayMap.has(key)) {
                    birthdayMap.set(key, {
                        id: `birthday-${key}`,
                        title: b.employeeName || b.employeeEmail || '',
                        start: toCalendarStartDate(day.date),
                        end: toCalendarEndDate(day.date),
                        allDay: true,
                        resource: { type: 'birthday', sourceDate: day.date, ...b }
                    });
                }
            }
            for (const a of day.anniversaries || []) {
                const key = `${day.date}-an-${a.employeeEmail}`;
                if (!anniversaryMap.has(key)) {
                    anniversaryMap.set(key, {
                        id: `anniversary-${key}`,
                        title: a.employeeName || a.employeeEmail || '',
                        start: toCalendarStartDate(day.date),
                        end: toCalendarEndDate(day.date),
                        allDay: true,
                        resource: { type: 'anniversary', sourceDate: day.date, ...a }
                    });
                }
            }
        }
        // Holidays first, then birthdays/anniversaries, then leaves
        return [
            ...holidayMap.values(),
            ...birthdayMap.values(),
            ...anniversaryMap.values(),
            ...leaveMap.values()
        ];
    }, [days]);

    const leaveTypes = useMemo(() => {
        const types = new Set();
        for (const day of days) {
            for (const l of day.leaves || []) {
                const leaveType = getLeaveTypeKey(l);
                if (leaveType && leaveType !== 'Holiday' && leaveType !== 'Leave') {
                    types.add(leaveType);
                }
            }
        }
        return [...types].sort((a, b) => {
            const aIdx = LEAVE_TYPE_ORDER.indexOf(a);
            const bIdx = LEAVE_TYPE_ORDER.indexOf(b);
            if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
            if (aIdx === -1) return 1;
            if (bIdx === -1) return -1;
            return aIdx - bIdx;
        });
    }, [days]);

    const filteredEvents = useMemo(() => {
        if (!selectedLeaveType) return allEvents;
        if (selectedLeaveType === HOLIDAY_FILTER_VALUE) {
            return allEvents.filter((e) => e.resource?.type === 'holiday');
        }
        if (selectedLeaveType === BIRTHDAY_FILTER_VALUE) {
            return allEvents.filter((e) => e.resource?.type === 'birthday');
        }
        if (selectedLeaveType === ANNIVERSARY_FILTER_VALUE) {
            return allEvents.filter((e) => e.resource?.type === 'anniversary');
        }
        return allEvents.filter((e) => getLeaveTypeKey(e) === selectedLeaveType);
    }, [allEvents, selectedLeaveType]);

    const isMonthFetchPending = !monthDate.isSame(requestMonthDate, 'month');
    const showCalendarLoader = isCalendarsLoading || isLoading || isMonthFetchPending;

    const eventPropGetter = useCallback((event) => {
        const kind = event.resource?.type;
        const themeMode = theme.palette.mode;
        const colorMap = themeMode === 'dark' ? DARK_LEAVE_TYPE_COLOR_MAP : LEAVE_TYPE_COLOR_MAP;
        const makeStyle = (c) => ({
            style: {
                backgroundColor: c.bg,
                borderLeft: `3px solid ${c.dot || c.border}`,
                borderTop: `1px solid ${c.border}40`,
                borderRight: `1px solid ${c.border}40`,
                borderBottom: `1px solid ${c.border}40`,
                color: c.text,
                borderRadius: 4,
                fontWeight: 600,
            }
        });
        if (kind === 'holiday')     return makeStyle(colorMap.Holiday);
        if (kind === 'birthday')    return makeStyle(colorMap.Birthday);
        if (kind === 'anniversary') return makeStyle(colorMap['Work Anniversary']);
        const style = makeStyle(getLeaveTypeColor(event, themeMode === 'dark'));
        const sliceFraction = Number(event.resource?.sliceFraction) || 1;

        if (sliceFraction < 1) {
            style.style.width = `${sliceFraction * 100}%`;
            style.style.maxWidth = `${sliceFraction * 100}%`;
            style.style.marginRight = 'auto';
        }

        return style;
    }, [theme]);

    const dayPropGetter = useCallback((date) => {
        const dow = date.getDay();
        const style = {};

        if (weekends.includes(dow)) {
            style.backgroundColor =
                theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.04)'
                    : 'rgba(0,0,0,0.025)';
        }

        return Object.keys(style).length > 0 ? { style } : {};
    }, [weekends, theme]);

    const handleSelectSlot = useCallback(({ start }) => {
        const dateStr = dayjs(start).format('YYYY-MM-DD');
        const day = days.find((d) => d.date === dateStr);
        if (day) setSelectedDay(day);
    }, [days]);

    const handleShowMore = useCallback((events, date) => {
        const dateStr = dayjs(date).format('YYYY-MM-DD');
        const day = days.find((d) => d.date === dateStr);
        if (day) setSelectedDay(day);
    }, [days]);

    // Clicking a birthday/anniversary event fires the celebration overlay.
    // All other event types fall through to open the day-detail dialog.
    const handleSelectEvent = useCallback((event) => {
        const kind = event.resource?.type;
        if (kind === 'birthday' || kind === 'anniversary') {
            const name = event.title || event.resource?.employeeName || '';
            setCelebration({
                type: kind,
                name,
                yearsOfService: kind === 'anniversary' ? event.resource?.yearsOfService : undefined,
            });
        } else {
            const sourceDate = event.resource?.sourceDate || event.start;
            const dateStr = dayjs(sourceDate).format('YYYY-MM-DD');
            const day = days.find((d) => d.date === dateStr);
            if (day) setSelectedDay(day);
        }
    }, [days]);

    // Eye icon in each day cell header — clicking opens the detail dialog
    const CustomDateHeader = useCallback(
        ({ date, label }) => {
            const dateStr = dayjs(date).format('YYYY-MM-DD');
            const day = days.find((d) => d.date === dateStr);
            const hasContent =
                day &&
                ((day.leaves?.length ?? 0) +
                    (day.holidays?.length ?? 0) +
                    (day.birthdays?.length ?? 0) +
                    (day.anniversaries?.length ?? 0) >
                    0);
            const isToday = dayjs(date).isSame(dayjs(), 'day');
            const isWeekend = weekends.includes(dayjs(date).day());
            const labelColor = isToday
                ? theme.palette.common.white
                : isWeekend
                  ? theme.palette.text.disabled
                  : theme.palette.text.secondary;
            const iconColor = isToday
                ? theme.palette.primary.main
                : isWeekend
                  ? theme.palette.text.disabled
                  : theme.palette.text.secondary;
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 0.5,
                        minHeight: 24
                    }}
                >
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: isToday ? 700 : 600,
                            color: labelColor,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: isToday ? 24 : 'auto',
                            height: isToday ? 24 : 'auto',
                            padding: isToday ? '0 7px' : 0,
                            borderRadius: isToday ? 999 : 0,
                            backgroundColor: isToday ? theme.palette.primary.main : 'transparent',
                            lineHeight: 1
                        }}
                    >
                        {label}
                    </span>
                    {hasContent && (
                        <Tooltip title='View details' placement='top'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDay(day);
                                }}
                                sx={{
                                    p: 0.2,
                                    color: iconColor,
                                    '&:hover': { color: iconColor, bgcolor: 'transparent' }
                                }}
                            >
                                <VisibilityOutlinedIcon sx={{ fontSize: 14, fontWeight: 700 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        },
        [days, theme, weekends]
    );

    const handleMonthDateChange = useCallback((v) => {
        if (v && dayjs(v).isValid()) setMonthDate(dayjs(v).startOf('month'));
    }, []);

    const handlePrevMonth = useCallback(() => {
        setMonthDate((prev) => dayjs(prev).subtract(1, 'month').startOf('month'));
    }, []);

    const handleNextMonth = useCallback(() => {
        setMonthDate((prev) => dayjs(prev).add(1, 'month').startOf('month'));
    }, []);

    return (
        <Box sx={{ position: 'relative', width: '-webkit-fill-available' }}>
            <ContentContainer
                title='Calendar'
                sx={{ width: '100%' }}
                headerBg={appTheme.palette.background.white}
                AdornmentComponent={
                    <Filters
                        monthDate={monthDate}
                        onMonthDateChange={handleMonthDateChange}
                        onPrevMonth={handlePrevMonth}
                        onNextMonth={handleNextMonth}
                        calendars={calendars}
                        isCalendarsLoading={isCalendarsLoading}
                        calendarId={calenderId}
                        onCalendarChange={setCalenderId}
                        leaveTypes={leaveTypes}
                        selectedLeaveType={selectedLeaveType}
                        onLeaveTypeChange={setSelectedLeaveType}
                    />
                }
            >
                <Box sx={{ width: '100%', p: { xs: 1, md: 2 } }}>
                    {error && (
                        <Typography color='error' variant='body2' sx={{ mb: 1 }}>
                            {error}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Tooltip
                            arrow
                            placement='bottom-start'
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: theme.palette.background.paper,
                                        color: theme.palette.text.primary,
                                        border: `1px solid ${theme.palette.divider}`,
                                        boxShadow: theme.shadows[3]
                                    }
                                },
                                arrow: {
                                    sx: {
                                        color: theme.palette.background.paper,
                                        '&:before': {
                                            border: `1px solid ${theme.palette.divider}`
                                        }
                                    }
                                }
                            }}
                            title={
                                <Box sx={{ py: 0.5 }}>
                                    {legendItems.map(({ label, color }) => (
                                        <Box
                                            key={label}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                py: 0.4
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: '50%',
                                                    backgroundColor: color.dot || color.border,
                                                    flexShrink: 0
                                                }}
                                            />
                                            <Typography
                                                variant='caption'
                                                sx={{ color: 'text.primary' }}
                                            >
                                                {label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            }
                        >
                            <Stack
                                direction='row'
                                spacing={0.75}
                                alignItems='center'
                                sx={{ color: 'text.secondary', cursor: 'help' }}
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                <Typography variant='caption' sx={{ fontWeight: 600 }}>
                                    Color guide
                                </Typography>
                            </Stack>
                        </Tooltip>
                    </Box>

                    {/* react-big-calendar with MUI-matched styles */}
                    <Box
                        sx={{
                            position: 'relative',
                            height: { xs: 520, sm: 600, md: 780, lg: 800 },
                            '@media (min-width: 2000px)': {
                                height: 1058
                            },
                            '& .rbc-calendar': {
                                fontFamily: theme.typography.fontFamily,
                                fontSize: 13,
                                color: theme.palette.text.primary
                            },
                            '& .rbc-month-view': {
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden'
                            },
                            '& .rbc-header': {
                                fontWeight: 600,
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                padding: '8px 4px',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                color: theme.palette.text.secondary,
                                background: theme.palette.background.default
                            },
                            '& .rbc-header + .rbc-header': {
                                borderLeft: `1px solid ${theme.palette.divider}`
                            },
                            '& .rbc-month-row': {
                                borderTop: `1px solid ${theme.palette.divider}`,
                                [theme.breakpoints.up('md')]: {
                                    minHeight: 120
                                },
                                '@media (min-width: 2000px)': {
                                    minHeight: 173
                                }
                            },
                            '& .rbc-day-bg + .rbc-day-bg': {
                                borderLeft: `1px solid ${theme.palette.divider}`
                            },
                            '& .rbc-day-bg': {
                                background: theme.palette.background.paper,
                                transition: 'background 0.1s'
                            },
                            '& .rbc-day-bg:not(.rbc-off-range-bg)': {
                                cursor: 'pointer'
                            },
                            '& .rbc-off-range-bg': {
                                background: theme.palette.action.hover
                            },
                            '& .rbc-today': {
                                background: `${theme.palette.primary.main}12`
                            },
                            '& .rbc-date-cell': {
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '4px 8px',
                                color: theme.palette.text.secondary,
                                '@media (min-width: 2000px)': {
                                    padding: '6px 8px',
                                    fontSize: 13
                                }
                            },
                            '& .rbc-date-cell.rbc-current': {
                                fontWeight: 700,
                                color: theme.palette.primary.main
                            },
                            '& .rbc-date-cell.rbc-off-range': {
                                color: theme.palette.text.disabled
                            },
                            '& .rbc-event': {
                                padding: '1px 5px',
                                borderRadius: 4,
                                fontSize: 11,
                                cursor: 'pointer',
                                '@media (min-width: 2000px)': {
                                    padding: '3px 6px',
                                    fontSize: 12,
                                    marginTop: '1px',
                                    marginBottom: '1px'
                                }
                            },
                            '& .rbc-event:focus': {
                                outline: `2px solid ${theme.palette.primary.main}`
                            },
                            '& .rbc-show-more': {
                                color: theme.palette.primary.dark,
                                fontWeight: 700,
                                fontSize: 11,
                                background: 'transparent',
                                paddingLeft: '4px'
                            },
                            '& .rbc-row-segment': {
                                padding: '0 2px 2px'
                            },
                            '& .rbc-overlay': {
                                background: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                                boxShadow: theme.shadows[4],
                                zIndex: 1300,
                                maxHeight: '60dvh',
                                overflowY: 'auto',
                                overflowX: 'hidden'
                            },
                            '& .rbc-overlay-header': {
                                fontWeight: 700,
                                fontSize: 13,
                                padding: '8px 12px',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                color: theme.palette.text.primary
                            }
                        }}
                    >
                        {showCalendarLoader && <ContentCircularLoader />}
                        <Calendar
                            localizer={localizer}
                            events={filteredEvents}
                            view='month'
                            views={['month']}
                            date={monthDate.toDate()}
                            onNavigate={(date) =>
                                setMonthDate(dayjs(date).startOf('month'))
                            }
                            toolbar={false}
                            allDayMaxRows={5}
                            onShowMore={handleShowMore}
                            doShowMoreDrillDown={false}
                            eventPropGetter={eventPropGetter}
                            dayPropGetter={dayPropGetter}
                            onSelectEvent={handleSelectEvent}
                            onSelectSlot={handleSelectSlot}
                            selectable
                            components={{
                                event: CalEventContent,
                                month: { dateHeader: CustomDateHeader }
                            }}
                        />
                    </Box>
                </Box>
            </ContentContainer>

            {/* Day details dialog */}
            <Dialog
                open={Boolean(selectedDay)}
                onClose={() => setSelectedDay(null)}
                maxWidth='sm'
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '60dvh'
                    }
                }}
            >
                {selectedDay && (
                    <>
                        <DialogTitle
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 2,
                                px: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Box>
                                <Typography
                                    variant='overline'
                                    sx={{ color: 'text.secondary', letterSpacing: 1.5, fontSize: 10, display: 'block', lineHeight: 1.4 }}
                                >
                                    {dayjs(selectedDay.date).format('dddd')}
                                </Typography>
                                <Typography variant='h6' sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                                    {dayjs(selectedDay.date).format('MMMM D, YYYY')}
                                </Typography>
                            </Box>
                            <IconButton size='small' onClick={() => setSelectedDay(null)} sx={{ color: 'text.secondary' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ overflowY: 'auto' }}>
                            <DayDetails day={selectedDay} onCelebrate={setCelebration} />
                        </DialogContent>
                    </>
                )}
            </Dialog>

            {/* Celebration overlay */}
            {celebration && (
                <CelebrationOverlay
                    celebration={celebration}
                    onClose={() => setCelebration(null)}
                />
            )}
        </Box>
    );
};

export default LeaveCalendarView;
